import React, { useState, useEffect } from 'react';

import CustomDatePicker, { DateRangeOptions } from '../../components/CustomDatePicker';
import GenericTable from '../../components/GenericTable';
import LoaderButton from '../../components/LoaderButton';

import { getShopLog, getShopNames, getUserNames } from '../../libs/db-lib';

import { calculateStartAndEndDate, updateLogData } from '../../libs/utils';

import { ExternalLogSubcomponent, LogSubcomponent } from '../../libs/tables/subComponents';
import { baseLogColumns } from '../../libs/tables/columnDefinitions';

import { Log } from '../../types';
import { storeClipboard } from '../../libs/utils-ts';

import { useUser } from '../../context/User';
import { useOemRegion } from '../../context/OemRegion';


const ShopLogStandalone = (props: any) => {
  const { user } = useUser();
  const { oemInfo } = useOemRegion();

  const [log, setLog] = useState(props.shop?.shopLog || [] as Log[]);
  const [showLog, setShowLog] = useState(props.shop?.shopLog?.length ? true : false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [rangePicked, setRangePicked] = useState<DateRangeOptions>('last30Days');
  const isNotExternal = user?.userType !== 'EXTERNAL';

  const { shop, handleShowAlert, onChange } = props;

  const topRef = React.createRef<HTMLDivElement>();

  useEffect(() => {
    const updateAndGroupLogs = async () => {
      await updateLogData(log, oemInfo);
      let groups = log.reduce((group: any, entry: any) => {
        group[entry.actionCode] = group[entry.actionCode] || [];
        group[entry.actionCode].push(entry);
        return group;
      }, Object.create(null));
    }
    updateAndGroupLogs();
  }, [log]);

  useEffect(() => {
    if (topRef && topRef.current && log.length !== 0 && shouldScroll) {
      topRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setShouldScroll(false);
    }
  }, [log, topRef, shouldScroll]);

  const fetchLogs = async (shopID: string) => {
    setIsLoading(true);
    setShowLog(false);
    const { startDateStr, endDateStr } = calculateStartAndEndDate(rangePicked);

    let result;

    try {
      result = await getShopLog(shopID, startDateStr, endDateStr);
      if (!result || Object.hasOwn(result, 'error')) {
        if ([401, 403].indexOf(result.error.status) === -1) {
          handleShowAlert('Error', result.error);
        }
        return;
      } else if (result.logEntries.length === 0) {
        handleShowAlert(
          'Get Logs',
          <span>
            No logs found for this shop: <strong>{isNotExternal ? shopID : shop?.shopName}</strong> in that date
            range.
          </span>
        );
      } else {
        
        const shopIDs: string[] = [];
        const userIDs: string[] = [];

        // Build list of shopIDs, userIDs, and toolIDs without duplicates
        result.logEntries.forEach((log) => {
          if (log.shopID && !shopIDs.includes(log.shopID)) {
            shopIDs.push(log.shopID);
          }
          if (log.userID && !userIDs.includes(log.userID)) {
            userIDs.push(log.userID);
          }
        });

        // Retrieve shops, users, and tools info
        const promises = [
          getShopNames(shopIDs),
          getUserNames(userIDs),
        ];
        
        const results = await Promise.all(promises);
        
        const shopNames = results[0];
        const userNames = results[1];

        // Inject shop and user names into logs
        result.logEntries.forEach((log) => {
          if (log.shopID) {
            log.shopName = shopNames[log.shopID];
          }
          if (log.userID) {
            log.userName = userNames[log.userID];
          }
        });

        let shops = JSON.parse(
          window.localStorage.getItem('pageData') as string
        );

        const shopKey = Object.keys(shops ?? {}).find(
          (key) => shops[key]?.shopID === shop.shopID
        );

        if (shopKey !== undefined) {
          shops[shopKey]['shopLog'] = result.logEntries;
          storeClipboard(shops);
        }

        setLog(result.logEntries);
        setShowLog(true);
      }
    } catch (e: any) {
      if (!e || Object.hasOwn(e, 'error')) {
        if ([401, 403].indexOf(e.error.status) === -1) {
          handleShowAlert('Error', e.message);
        } else {
          return;
        }
      }
    } finally {
      setIsLoading(false);
      onChange(result.logEntries);
    }
  };

  return (
    <React.Fragment>
      <div className="l-flex-between l-flex-align-center">
        <div className="c-field">
          <label htmlFor="query" className="c-field__label u-margin-top-large">
            Date Range
          </label>
          <CustomDatePicker
            rangePicked={rangePicked}
            setRangePicked={setRangePicked}
          />
        </div>
        <LoaderButton
          onClick={() => {
            fetchLogs(shop.shopID);
            const shopInfoElement = document.getElementById(shop.shopID);
            if (shopInfoElement) {
              shopInfoElement.style.maxHeight = 'fit-content';
            }
          }}
          type="button"
          isLoading={isLoading}
          text="Get Logs"
          loadingText="Getting logs..."
        />
      </div>
      {showLog && !isLoading && (
        <>
          <div className="u-text-center" ref={topRef}>
            <h2>
              Log Table For Shop:{' '}
              <span className="u-font-mono">{`${shop.shopName} ${isNotExternal ? `(${shop.shopID})` : ''}`}</span>
            </h2>
          </div>
          <GenericTable
            data={log || []}
            filterKeys={['actionMessage', 'actionCode', 'actionDate']}
            columnDefs={isNotExternal ? baseLogColumns.slice(1) : baseLogColumns.slice(1, 4)}
            defaultSorted={[
              {
                id: 'actionDate',
                desc: true,
              },
            ]}
            savedColumnsId={'fullLogTableColumns'}
            subComponentRenderer={isNotExternal ? LogSubcomponent : ExternalLogSubcomponent} // External Support Portal Phase 1. Adjust to user ExternalLogSubcomponent without IDs and with properly populated fields in future phase.
            getTrProps={(state: any, rowInfo: any, column: any) => {
              if (rowInfo) {
                if (rowInfo.original.actionCode === 'UPDATE_USER_INFO') {
                  return {
                    className: 'hide-expander',
                  };
                }
              }
              return {};
            }}
          />
        </>
      )}
    </React.Fragment>
  );
};

export default ShopLogStandalone;
